import type {
  AssetInput,
  DocumentAssetInput,
  ImageAssetInput,
  ImageMetadataInput,
  LinkAttachmentInput,
  UserTagInput,
  VideoAssetInput,
} from './graphqlTypes'
import { getMixedMediaCapabilityForService } from '~publish/components/Composer/capability'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import { Document } from '~publish/legacy/composer/composer/entities/Document'
import type { DocumentTitle } from '~publish/legacy/composer/composer/entities/Document'
import type {
  Gif,
  Image,
  Link,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import type { UserTag } from '~publish/legacy/composer/composer/stores/types'
import { ensureUrlProtocol } from '~publish/legacy/composer/composer/utils/StringUtils'

// The media fields shared by a per-channel `Draft` (root post) and a thread
// `Post` — enough to build assets. A thread `Post` has no `document`.
export type MediaSource = {
  link: Link | null
  images: Image[]
  video: Video | null
  gif: Gif | null
  // A Draft's document is `DocumentTitle | Document | null`; a thread Post has none.
  document?: DocumentTitle | Document | null
}

// Ordered `oneOf` `AssetInput[]`. Precedence: image(s) | video | document |
// gif. Shared by the root draft and each thread entry (see mapThread), and by
// both post creation and edits (via mapSharedPostFields, used by
// mapToCreatePostInput.ts and mapToEditPostInput.ts alike) — both server-side
// paths ignore `thumbnailUrl` for images, so it's skipped here (see mapImage).
export function mapMediaAssets(
  source: MediaSource,
  { canTag }: { canTag: boolean },
): AssetInput[] {
  if (source.images.length > 0) {
    return source.images.map((image) => ({
      image: mapImage(image, canTag, { includeThumbnailUrl: false }),
    }))
  }

  if (source.video) {
    return [{ video: mapVideo(source.video) }]
  }

  const document = source.document ?? null
  if (Document.isDocument(document)) {
    return [{ document: mapDocument(document) }]
  }

  // GIF has no dedicated AssetInput variant, so it is modelled as an image.
  if (source.gif) {
    return [{ image: mapGif(source.gif) }]
  }

  return []
}

// Maps every entry in draft.assets, preserving order. Feeds both post
// creation and edits (see mapAssets below), so images skip the redundant
// `thumbnailUrl` (see mapImage).
function mapMixedMediaAssets(draft: Draft): AssetInput[] {
  const canTag = draft.service.canHaveUserTags
  const assets: AssetInput[] = []
  for (const asset of draft.assets) {
    switch (asset.kind) {
      case 'image':
        assets.push({
          image: mapImage(asset.image, canTag, { includeThumbnailUrl: false }),
        })
        break
      case 'video':
        assets.push({ video: mapVideo(asset.video) })
        break
      case 'gif':
        // GIF has no dedicated AssetInput variant, so it is modelled as an image.
        assets.push({ image: mapGif(asset.gif) })
        break
      case 'document':
        // An in-flight DocumentTitle placeholder has no url yet; skip it
        // rather than send an unsaveable document.
        if (Document.isDocument(asset.document)) {
          assets.push({ document: mapDocument(asset.document) })
        }
        break
    }
  }
  return assets
}

export function mapLinkAttachment(
  draft: Draft,
): LinkAttachmentInput | undefined {
  if (!draft.hasLinkAttachmentEnabled() || !draft.link) {
    return undefined
  }
  return toLinkAttachmentInput(draft.link)
}

export function toLinkAttachmentInput(link: Link): LinkAttachmentInput {
  const { url, title, description, thumbnail } = link
  return {
    url,
    ...(title != null ? { title } : {}),
    ...(description != null ? { description } : {}),
    ...(thumbnail?.url != null ? { thumbnail: { url: thumbnail.url } } : {}),
  }
}

// Bluesky rejects a scheme-less embed uri with "Invalid uri (...) at
// $.record.embed.external.uri", so its link attachment needs the same protocol
// normalization the editor applies to typed links (see findAndWrapNewLinks).
export function ensureLinkAttachmentProtocol(
  linkAttachment: LinkAttachmentInput,
): LinkAttachmentInput {
  return { ...linkAttachment, url: ensureUrlProtocol(linkAttachment.url) }
}

// A per-channel `Draft`'s assets (the root post). Empty when the draft sends a
// link attachment: the server rejects one next to non-empty `assets`.
export function mapAssets(draft: Draft): AssetInput[] {
  if (mapLinkAttachment(draft) !== undefined) {
    return []
  }
  const capability = getMixedMediaCapabilityForService(draft.service.name)
  if (capability.canMixMediaTypes) {
    return mapMixedMediaAssets(draft)
  }
  return mapMediaAssets(draft, { canTag: draft.service.canHaveUserTags })
}

export function mapImage(
  image: Image,
  canTag: boolean,
  // The server ignores `dimensions` (deprecated) and, for both post creation
  // and edits, ignores `thumbnailUrl` too (it always derives the thumbnail
  // from `url`). Draft creation has no such fallback, so callers that build a
  // `DraftContentInput` must keep sending it (see mapToDraftContentInput.ts).
  { includeThumbnailUrl = true }: { includeThumbnailUrl?: boolean } = {},
): ImageAssetInput {
  const userTags =
    canTag && image.userTags ? image.userTags.map(mapUserTag) : undefined
  // ImageMetadataInput.altText is non-null; legacy stored it nullable.
  const metadata: ImageMetadataInput = {
    altText: image.altText ?? '',
    ...(userTags ? { userTags } : {}),
  }

  return {
    url: image.url,
    // Legacy photo/picture/thumbnail all resolved to the image url.
    ...(includeThumbnailUrl ? { thumbnailUrl: image.url } : {}),
    metadata,
  }
}

export function mapGif(gif: Gif): ImageAssetInput {
  return {
    url: gif.url,
    thumbnailUrl: gif.stillGifUrl,
  }
}

// Legacy `{ username, x: string, y: string }` → `{ handle, x: Float, y: Float }`.
function mapUserTag(tag: UserTag): UserTagInput {
  return { handle: tag.username, x: Number(tag.x), y: Number(tag.y) }
}

export function mapVideo(video: Video): VideoAssetInput {
  // Legacy video `details` (duration/size/…) has no GraphQL home; server re-probes.
  // Only set the offset when it parses to a finite value (avoids NaN leaking in);
  // rounded because thumbnailOffset is a GraphQL Int, but thumbOffset can carry a
  // sub-millisecond float from `video.currentTime * 1000` upstream.
  const offset = Math.round(Number(video.thumbOffset))
  return {
    url: video.originalUrl || video.url,
    thumbnailUrl: video.thumbnail,
    metadata: {
      title: video.name,
      ...(Number.isFinite(offset) ? { thumbnailOffset: offset } : {}),
    },
  }
}

export function mapDocument(document: Document): DocumentAssetInput {
  // DocumentAssetInput url/title/thumbnailUrl are all non-null.
  return {
    url: document.url,
    title: document.title,
    thumbnailUrl: document.thumbnailUrl ?? '',
  }
}
